import { useState, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FaFlag } from 'react-icons/fa6';
import { MdOutlineFileDownload, MdClose, MdContentCopy } from 'react-icons/md';
import { useFundRequestContext } from '~/context/FundRequestProvider';
import RejectACHInfoModal from '~/components/FundRequests/modals/rejectACHInfo';
import { formatDateIfValid } from '~/utils';
import { approveACH, rejectACH } from '~/services/fund-requests';
import toast from 'react-hot-toast';
import RequestACHInfoModal from '../modals/requestACHInfo';
import { isPdfFile, isImageFile } from '~/components/FundRequests/utils/fileType';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up the worker for react-pdf - use stable CDN
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Simple PDF Lightbox Component
const PDFLightbox = ({
    isOpen,
    onClose,
    url,
    filename
}: {
    isOpen: boolean;
    onClose: () => void;
    url: string;
    filename: string;
}) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);

    // Detect file type based on filename
    const isPDF = isPdfFile(filename);
    const isImage = isImageFile(filename);

    function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
        setNumPages(numPages);
        setLoading(false);
    }

    function onDocumentLoadError(error: Error) {
        console.error('Error loading PDF:', error);
        setLoading(false);
    }

    const goToPrevPage = () => {
        setPageNumber(prev => Math.max(1, prev - 1));
    };

    const goToNextPage = () => {
        setPageNumber(prev => Math.min(numPages, prev + 1));
    };

    // Handle click outside to close
    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            switch (event.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    if (isPDF) {
                        event.preventDefault();
                        goToPrevPage();
                    }
                    break;
                case 'ArrowRight':
                    if (isPDF) {
                        event.preventDefault();
                        goToNextPage();
                    }
                    break;
                default:
                    break;
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => {
                document.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [isOpen, numPages, pageNumber, onClose, isPDF]);

    // Early return after all hooks are declared
    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-75"
            onClick={handleBackdropClick}
        >
            <div className="relative max-h-[95vh] max-w-[95vw] bg-white rounded-lg shadow-2xl overflow-hidden">
                {/* Header with controls */}
                <div className="sticky top-0 z-[100000] bg-white border-b border-gray-200 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {filename}
                            </h3>
                            {isPDF && numPages > 0 && (
                                <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                                    Page {pageNumber} of {numPages}
                                </span>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            {/* Download link */}
                            <a
                                href={url}
                                download={filename}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-800 transition-colors font-medium"
                                style={{
                                    color: '#2563EB',
                                    textDecoration: 'none',
                                    fontSize: '14px',
                                    zIndex: 100001
                                }}
                                title={`Download ${isPDF ? 'PDF' : 'File'}`}
                            >
                                <MdOutlineFileDownload size={16} />
                                Download {isPDF ? 'PDF' : 'File'}
                            </a>

                            <button
                                onClick={onClose}
                                className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 transition-colors"
                                title="Close viewer (ESC)"
                            >
                                <MdClose size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Navigation controls - only show for PDFs */}
                    {isPDF && numPages > 1 && (
                        <div className="flex justify-center gap-3 mt-4">
                            <button
                                onClick={goToPrevPage}
                                disabled={pageNumber <= 1}
                                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                                ← Previous
                            </button>

                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Page</span>
                                <input
                                    type="number"
                                    min="1"
                                    max={numPages}
                                    value={pageNumber}
                                    onChange={(e) => {
                                        const page = parseInt(e.target.value);
                                        if (page >= 1 && page <= numPages) {
                                            setPageNumber(page);
                                        }
                                    }}
                                    className="w-16 px-2 py-1 text-center border border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-600">of {numPages}</span>
                            </div>

                            <button
                                onClick={goToNextPage}
                                disabled={pageNumber >= numPages}
                                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                                Next →
                            </button>
                        </div>
                    )}

                    {/* Keyboard shortcuts hint */}
                    <div className="text-xs text-gray-500 text-center mt-2">
                        {isPDF ? 'Use ← → arrow keys to navigate • ESC to close • Click outside to close' : 'ESC to close • Click outside to close'}
                    </div>
                </div>

                {/* Content */}
                <div className="overflow-auto max-h-[calc(95vh-140px)] bg-gray-50 flex justify-center p-4">
                    {isPDF ? (
                        // PDF Content
                        <>
                            {loading && (
                                <div className="text-center p-8">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                                    <p className="text-gray-600">Loading PDF...</p>
                                </div>
                            )}

                            <Document
                                file={url}
                                onLoadSuccess={onDocumentLoadSuccess}
                                onLoadError={onDocumentLoadError}
                                loading={null}
                                error={
                                    <div className="text-center p-8">
                                        <p className="text-red-500 mb-2 text-lg">Failed to load PDF</p>
                                        <p className="text-gray-600 text-sm">Please check if the file is a valid PDF document.</p>
                                        <a
                                            href={url}
                                            download={filename}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors inline-block"
                                        >
                                            Try downloading instead
                                        </a>
                                    </div>
                                }
                            >
                                {!loading && (
                                    <Page
                                        key={`page_${pageNumber}`}
                                        pageNumber={pageNumber}
                                        renderTextLayer={true}
                                        renderAnnotationLayer={true}
                                        width={Math.min(800, window.innerWidth - 100)}
                                        className="shadow-lg border border-gray-200"
                                    />
                                )}
                            </Document>
                        </>
                    ) : isImage ? (
                        // Image Content
                        <div className="flex items-center justify-center">
                            <img
                                src={url}
                                alt={filename}
                                className="max-w-full max-h-full object-contain shadow-lg border border-gray-200 rounded"
                                onLoad={() => setLoading(false)}
                                onError={() => setLoading(false)}
                                style={{ maxHeight: 'calc(95vh - 200px)', maxWidth: 'calc(95vw - 100px)' }}
                            />
                        </div>
                    ) : (
                        // Unsupported file type
                        <div className="text-center p-8">
                            <p className="text-gray-600 mb-4">Preview not available for this file type.</p>
                            <a
                                href={url}
                                download={filename}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors inline-block"
                            >
                                Download File
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const PaymentOptions = () => {
    const { fundRequest, fundRequestDispatch } = useFundRequestContext();
    const [modalOpen, setModalOpen] = useState(false);
    const [requestModalOpen, setRequestModalOpen] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [verifyConfirmOpen, setVerifyConfirmOpen] = useState(false);
    const { control, watch } = useFormContext();
    const PaymentTypes = {
        CHECK: 'CHECK',
        ACH: 'ACH',
    };
    const paymentType = watch('payment_type');
    const [saving, setSaving] = useState(false);

    const achAvailable = !!fundRequest?.vendor?.ach_last_4;
    const achRequest = fundRequest.ach_request;

    const needsACH = paymentType === PaymentTypes.ACH && !achAvailable;

    const handleACHApprove = async () => {
        setVerifyConfirmOpen(false);
        setSaving(true);
        toast.promise(
            async () => {
                const updatedFundRequest = await approveACH(fundRequest.id);
                fundRequestDispatch({
                    type: 'set_fund_request',
                    payload: updatedFundRequest,
                });
                setSaving(false);
            },
            {
                loading: 'Approving ACH request...',
                success: 'ACH request approved',
                error: (err) => {
                    setSaving(false);
                    return `Error approving ACH request: ${err}`;
                },
            }
        );
    };

    // Show ACH request card if:
    // 1. ACH request exists and status is PENDING or SUBMITTED, OR
    // 2. Fund request status is resubmitted and ACH request exists (to show for review)
    // BUT NOT if the ACH request has been approved/verified
    const showACHRequest =
        achRequest && 
        achRequest.status !== 'APPROVED' &&
        (['PENDING', 'SUBMITTED'].includes(achRequest.status) || 
         fundRequest?.fund_request_status?.value === 'resubmitted');

    return (
        <div className='space-y-4'>
            <label htmlFor='payment_type' style={{ fontWeight: '600' }}>
                Payment Method
            </label>
            <div className='!mt-[2px] flex items-center gap-3'>
                <Controller
                    name='payment_type'
                    control={control}
                    render={({ field }) => (
                        <button
                            type='button'
                            className={`flex items-center gap-[8px] rounded-full border-[2px] px-[24px] py-[7px] text-[12px]/[16px] font-bold uppercase transition-colors duration-200 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] ${field.value === PaymentTypes.CHECK
                                ? 'border-[#00aeb3] bg-[#00aeb3]'
                                : ''
                                }`}
                            onClick={() => field.onChange(PaymentTypes.CHECK)}
                        >
                            Pay by Check
                        </button>
                    )}
                />

                <Controller
                    name='payment_type'
                    control={control}
                    render={({ field }) => (
                        <button
                            type='button'
                            className={`flex items-center gap-[8px] rounded-full border-[2px] px-[24px] py-[7px] text-[12px]/[16px] font-bold uppercase transition-colors duration-200 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] ${field.value === PaymentTypes.ACH
                                ? 'border-[#00aeb3] bg-[#00aeb3]'
                                : ''
                                }`}
                            onClick={() => field.onChange(PaymentTypes.ACH)}
                        >
                            Pay by Direct Deposit
                        </button>
                    )}
                />
            </div>
            <div>
                <label className='text-gray-700 mb-2 block text-sm font-bold'>
                    ACH Available
                </label>
                <div className='flex gap-3'>
                    <button
                        type='button'
                        className={`text-gray-900 border-gray-300 hover:bg-gray-100 rounded-full border-[2px] px-4 py-2 text-[12px]/[16px] font-bold transition-colors duration-200 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] ${achAvailable && 'border-[#00aeb3] bg-[#00aeb3]'
                            }`}
                    >
                        Yes
                    </button>
                    <button
                        type='button'
                        className={`text-gray-900 border-gray-300 hover:bg-gray-100 rounded-full border-[2px] px-4 py-2 text-[12px]/[16px] font-bold transition-colors duration-200 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] ${!achAvailable && 'border-[#00aeb3] bg-[#00aeb3]'
                            }`}
                    >
                        No
                    </button>
                    {showACHRequest && (
                        <button
                            type='button'
                            className='hover:bg-gray-100 rounded-full border border-danger bg-danger px-4 py-2 text-[12px]/[16px] font-bold text-white'
                        >
                            <FaFlag className='mt-0' color='white' /> Pending
                            Approval
                        </button>
                    )}
                    {needsACH && !showACHRequest && (
                        <button
                            type='button'
                            className='hover:bg-gray-100 rounded-full border border-danger bg-danger px-4 py-2 text-[12px]/[16px] font-bold text-white'
                        >
                            <FaFlag className='mt-0' color='white' /> Needs ACH
                            Info
                        </button>
                    )}
                </div>
                {needsACH &&
                    !['needs_info', 'resubmitted'].includes(
                        fundRequest?.fund_request_status?.value
                    ) && (
                        <div className='mt-5 rounded-lg border-[4px] border-danger p-4'>
                            <h3 className='mb-5 text-[18px]/[22px] font-bold uppercase'>
                                Direct Deposit (ACH) Information
                            </h3>
                            <div className='flex gap-3'>
                                <button
                                    type='button'
                                    className={`rounded-full border-[2px] border-warning bg-warning px-[24px] py-[11px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]`}
                                    onClick={() => setRequestModalOpen(true)}
                                    disabled={saving}
                                >
                                    Request ACH Info
                                </button>
                            </div>
                        </div>
                    )}
                {showACHRequest && (
                    <div className='mt-5 rounded-lg border-[4px] border-danger p-4'>
                        <h3 className='mb-5 text-[18px]/[22px] font-bold uppercase'>
                            Direct Deposit (ACH) Information
                        </h3>

                        <label className='mt-[16px] block text-[12px]/[14px] font-extrabold'>
                            Account Type
                            <span className='text-[10px]/[14px] text-danger'>
                                *
                            </span>
                        </label>
                        <div className='flex gap-[16px]'>
                            <div className='mt-[12px]'>
                                <div className='align-center flex gap-[12px]'>
                                    <input
                                        id='checking'
                                        type='radio'
                                        className='mt-[2px] h-[18px] w-[18px] shrink-0'
                                        readOnly={true}
                                        checked={
                                            achRequest.account_type ===
                                            'CHECKING'
                                        }
                                    />
                                    <label htmlFor='checking'>Checking</label>
                                </div>
                            </div>
                            <div className='mt-[12px]'>
                                <div className='align-center flex gap-[12px]'>
                                    <input
                                        id='savings'
                                        type='radio'
                                        className='mt-[2px] h-[18px] w-[18px] shrink-0'
                                        readOnly={true}
                                        checked={
                                            achRequest.account_type ===
                                            'SAVINGS'
                                        }
                                    />
                                    <label htmlFor='savings'>Savings</label>
                                </div>
                            </div>
                        </div>

                        {/* Routing Number */}
                        {achRequest.routing_number && (
                            <div className='mt-[16px]'>
                                <label className='block text-[12px]/[14px] font-extrabold mb-2'>
                                    Routing Number
                                </label>
                                <div className='flex items-center gap-2'>
                                    <div className='font-mono text-sm'>
                                        {achRequest.routing_number}
                                    </div>
                                    <button
                                        type='button'
                                        onClick={() => {
                                            navigator.clipboard.writeText(achRequest.routing_number);
                                            toast.success('Routing number copied to clipboard');
                                        }}
                                        className='p-2 rounded transition-colors'
                                        style={{
                                            color: '#00AEB3'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#E6F7F8';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                        title='Copy routing number'
                                    >
                                        <MdContentCopy size={18} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Account Number */}
                        {achRequest.account_number && (
                            <div className='mt-[16px]'>
                                <label className='block text-[12px]/[14px] font-extrabold mb-2'>
                                    Account Number
                                </label>
                                <div className='flex items-center gap-2'>
                                    <div className='font-mono text-sm'>
                                        {achRequest.account_number}
                                    </div>
                                    <button
                                        type='button'
                                        onClick={() => {
                                            navigator.clipboard.writeText(achRequest.account_number);
                                            toast.success('Account number copied to clipboard');
                                        }}
                                        className='p-2 rounded transition-colors'
                                        style={{
                                            color: '#00AEB3'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#E6F7F8';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                        title='Copy account number'
                                    >
                                        <MdContentCopy size={18} />
                                    </button>
                                </div>
                            </div>
                        )}

                        <label className='mt-[16px] block text-[12px]/[14px] font-extrabold'>
                            I will be providing one of the following:
                            <span className='text-[10px]/[14px] text-danger'>
                                *
                            </span>
                        </label>
                        <div className='mb-3 flex gap-[16px]'>
                            <div className='mt-[12px] shrink-0'>
                                <div className='align-center flex gap-[12px]'>
                                    <input
                                        id='check'
                                        type='radio'
                                        className='mt-[2px] h-[18px] w-[18px] shrink-0'
                                        readOnly={true}
                                        checked={
                                            achRequest.document_type ===
                                            'VOID_CHECK'
                                        }
                                    />
                                    <label htmlFor='check'>
                                        A voided check
                                    </label>
                                </div>
                            </div>
                            <div className='mt-[12px]'>
                                <div className='align-center flex gap-[12px]'>
                                    <input
                                        id='letter'
                                        type='radio'
                                        className='mt-[2px] h-[18px] w-[18px] shrink-0'
                                        readOnly={true}
                                        checked={
                                            achRequest.document_type ===
                                            'BANK_LETTER'
                                        }
                                    />
                                    <label htmlFor='bank_letter'>
                                        Letter or screenshot from bank stating
                                        account and routing number
                                    </label>
                                </div>
                            </div>
                        </div>

                        <button
                            type='button'
                            className='mt-2 inline-flex items-center gap-2 rounded-full bg-primary px-2 text-[12px] cursor-pointer hover:bg-primary-dark transition-colors'
                            onClick={() => setLightboxOpen(true)}
                        >
                            <span>{achRequest.ach_document_filename}</span>
                            <MdOutlineFileDownload />
                        </button>

                        <p className='mb-3 mt-3 italic'>
                            {formatDateIfValid(
                                achRequest.created_date,
                                'MM/dd/yy'
                            )}{' '}
                            User agreed to terms and conditions
                        </p>

                        <div className='flex gap-3'>
                            <button
                                type='button'
                                className={`rounded-full border-[2px] border-warning bg-warning px-[24px] py-[11px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]`}
                                onClick={() => setVerifyConfirmOpen(true)}
                                disabled={saving}
                            >
                                Verify ACH Info in MIP
                            </button>
                            <button
                                type='button'
                                className={`rounded-full border-[2px] border-danger px-[24px] py-[11px] text-[14px]/[18px] font-bold uppercase text-danger transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] hover:text-black`}
                                onClick={() => setModalOpen(true)}
                                disabled={saving}
                            >
                                Reject ACH Info
                            </button>
                        </div>

                        <div className='mt-3 font-bold'>
                            *ACH info must be entered in MIP before verifying
                        </div>

                        <RejectACHInfoModal
                            modalOpen={modalOpen}
                            setModalOpen={setModalOpen}
                        />

                        {/* <div className='mt-3 font-bold text-danger'>
                            *Issue with data in MIP
                        </div> */}
                    </div>
                )}
                {achRequest && achRequest.status === 'APPROVED' && (
                    <div className='mt-5 rounded-lg border-[4px] p-6' style={{ borderColor: '#AFC12D', backgroundColor: '#FFFFFF' }}>
                        <h3 className='text-[18px]/[22px] font-bold uppercase mb-2'>
                            Direct Deposit (ACH) Information
                        </h3>
                        <p className='text-[18px]/[22px] font-bold' style={{ color: '#AFC12D' }}>
                            Verified
                        </p>
                    </div>
                )}
                {achAvailable && paymentType == 'CHECK' && (
                    <div className='mt-5 font-bold text-danger'>
                        *Confirm Pay by Check
                    </div>
                )}
                <RequestACHInfoModal
                    modalOpen={requestModalOpen}
                    setModalOpen={setRequestModalOpen}
                />

                {showACHRequest && achRequest?.ach_document_url && (
                    <PDFLightbox
                        isOpen={lightboxOpen}
                        onClose={() => setLightboxOpen(false)}
                        url={achRequest.ach_document_url}
                        filename={achRequest.ach_document_filename || 'ACH Document'}
                    />
                )}

                {/* Verify ACH Confirmation Modal */}
                {verifyConfirmOpen && (
                    <div className='fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50'>
                        <div className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'>
                            <button
                                className='text-gray-600 hover:text-gray-900 absolute right-4 top-4'
                                onClick={() => setVerifyConfirmOpen(false)}
                            >
                                <MdClose size={24} />
                            </button>
                            <div className='p-8'>
                                <h2 className='mb-4 text-[22px] font-bold text-danger'>
                                    Stop! Have you downloaded the ACH backup?
                                </h2>
                                <p className='mb-6 text-sm text-gray-700'>
                                    ACH information will no longer be available for download once it is verified
                                </p>
                                <div className='flex justify-start gap-4'>
                                    <button
                                        className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                                        onClick={() => setVerifyConfirmOpen(false)}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className='rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                                        onClick={handleACHApprove}
                                        disabled={saving}
                                    >
                                        {saving ? 'Verifying...' : 'Yes, Verify'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentOptions;
